import { FC, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { LeaderboardHeader } from '../components/leaderboard-header/leaderboard-header';
import { LeaderboardLine, LeaderboardYouLine } from '../components/leaderborad-line';
import { RulesModal } from '../components/rules-modal/rules-modal';
import { useLeaderboard } from '../hooks/use-leaderboard';
import { t } from 'i18next';

import MobileLayout from 'shared/components/layout/mobile-layout';
import { ConnectWallet } from 'shared/components/sections/connect-wallet/connect-wallet';
import PrimaryButton from 'shared/components/ui/PrimaryButton/PrimaryButton';
import { OptimizedImage } from 'shared/components/ui/optimized-image/optimized-image';
import { RoomFooterText } from 'shared/components/ui/room-footer-text/room-footer-text';
import { ROUTES } from 'shared/constants/routes';
import { useAccount } from 'shared/hooks/use-web3';
import useMediaQuery from 'shared/hooks/useMediaQuery';
import { useUIStore } from 'shared/store/uiStore';

import leaderboard from 'assets/png/leaderboard.png?format=webp;avif&as=picture';

// constants for windowing
const ROW_HEIGHT = 48;
const ROW_HEIGHT_MOBILE = 32;
const WINDOW_SIZE = 30; // number of rows rendered at once

export const LeaderBoardPage: FC = () => {
  const { data, loading, error } = useLeaderboard();
  const navigate = useNavigate();
  const { address, isConnected } = useAccount();
  const { connectWalletModal } = useUIStore();
  const [start, setStart] = useState(0);
  const [isOpenRulesModal, setIsOpenRulesModal] = useState<boolean>(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery('(max-width: 448px)');

  const handleScroll = () => {
    if (!containerRef.current) return;
    const scrollTop = containerRef.current.scrollTop;
    const newStart = Math.floor(scrollTop / rowHeight);
    if (newStart !== start) {
      setStart(newStart);
    }
  };

  const openRulesModal = () => {
    setIsOpenRulesModal(true);
  };
  if (loading || !data) {
    return null;
  }

  const end = Math.min(start + WINDOW_SIZE, data.length);
  const visible = data.slice(start, end);

  const renderedRows = visible.length;
  const rowHeight = isMobile ? ROW_HEIGHT_MOBILE : ROW_HEIGHT;
  const fakeHeight = start * rowHeight + renderedRows * rowHeight;

  const index =
    data.findIndex((p) => p.walletAddress?.toLowerCase() === address?.toLowerCase()) ?? -1;
  const me = index >= 0 ? data[index] : undefined;

  if (connectWalletModal.show && !isConnected) {
    return (
      <ConnectWallet title={'common.connectWallet'} description={'pages.dailyQuests.connectDesc'} />
    );
  }
  return (
    <MobileLayout
      footer={
        <div className="flex w-full flex-col items-center justify-center gap-2">
          <PrimaryButton
            text="common.play"
            disabled={false}
            onClick={() => navigate(ROUTES.LOBBY)}
            className="w-full"
            isAnimated
          />
          <RoomFooterText />
        </div>
      }
      showFloor={false}
      className="px-[0px] pb-[2.5rem] sm:pb-[2rem]"
      footerClassNames="md:hidden"
    >
      <div className="relative mx-auto flex h-full max-w-screen-2xl flex-col px-0 py-0">
        <div className="mb-4 flex flex-col items-center justify-center">
          <OptimizedImage
            imageData={leaderboard}
            alt="leaderboard"
            className="mx-auto h-full w-full max-w-[17.625rem] object-cover md:max-w-[30rem]"
          />
          {/* <p className="text-center text-sm font-bold md:text-base">{t('leaderboard.3000')}</p>
          <button
            className="text-sm font-bold underline hover:no-underline md:text-base"
            onClick={openRulesModal}
          >
            {t('leaderboard.check_rules')}
          </button> */}
        </div>

        <LeaderboardHeader />

        <div
          ref={containerRef}
          className="hide-scrollbar flex-1 overflow-y-auto"
          onScroll={handleScroll}
        >
          {/* fake container with dynamic height */}
          <div style={{ height: fakeHeight, position: 'relative' }}>
            <div style={{ transform: `translateY(${start * rowHeight}px)` }}>
              {visible.map((player, i) => (
                <LeaderboardLine key={player.walletAddress} player={player} index={start + i} />
              ))}
            </div>
          </div>
        </div>

        {me && <LeaderboardYouLine first={data?.[0]?.xp} player={me} index={index} />}
      </div>
      <RulesModal open={isOpenRulesModal} onClose={() => setIsOpenRulesModal(false)} />
    </MobileLayout>
  );
};
